'use client';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState, useRef, useCallback } from 'react';
import { VariantProps } from 'tailwind-variants';

import { sendGTMEvent } from '@/fo/api/gtm';
import ArrowIcon from '@/fo/icons/base/icons/arrow48.svg';
import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import BiodermaIcon from '@/fo/icons/base/logos/bioderma.svg';
import EsthedermIcon from '@/fo/icons/base/logos/esthederm.svg';
import EtatPurIcon from '@/fo/icons/base/logos/etat-pur.svg';
import { utilGetLinkRel } from '@/fo/util/get-link-rel';

import { styles } from './UiKitSectionImportantProjects.styles';

export interface Item {
  id: number;
  brand?: string;
  image: string;
  text: string;
  title?: React.ReactNode;
  colorTitle?: string;
  link: string;
  linkText?: string;
  linkColor?: string;
  iconColor?: string;
  showLinkButton?: boolean;
  isBlank?: boolean;
  gtmEvent?: string;
  gtmEventCategory?: string;
  gtmEventAction?: string;
  gtmEventLabel?: string;
}

type ContentText = {
  type: 'text';
  itemsTitle?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
  text?: string;
  linkUrl?: string;
  linkText?: string;
  items: Item[];
  autoplay?: boolean;
};

type ContentImage = {
  type: 'image';
  itemsTitle?: React.ReactNode;
  items: Item[];
  autoplay?: boolean;
};

const AUTOPLAY_TIMEOUT = 5000;
const RESUME_DELAY = 1000;

type Variants = VariantProps<typeof styles>;
export interface UiKitSectionImportantProjectsProps {
  className?: string;
  itemClass?: string;
  content: ContentText | ContentImage;
  variant?: Variants['variant'];
}

export function UiKitSectionImportantProjects({
  content,
  itemClass,
  className,
  variant
}: UiKitSectionImportantProjectsProps) {
  const slots = styles({ variant });

  const [activeIndex, setActiveIndex] = useState(0);
  const isHovered = useRef(false);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearAutoplayTimeout = () => {
    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }
  };

  const goToNextItem = useCallback(() => {
    if (isHovered.current) return;

    setActiveIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % content.items.length;
      return newIndex;
    });

    if (content.autoplay) {
      clearAutoplayTimeout();
      autoplayTimeoutRef.current = setTimeout(goToNextItem, AUTOPLAY_TIMEOUT);
    }
  }, [content.autoplay, content.items.length]);

  useEffect(() => {
    if (content.autoplay) {
      clearAutoplayTimeout();
      autoplayTimeoutRef.current = setTimeout(goToNextItem, AUTOPLAY_TIMEOUT);
    }

    return () => {
      clearAutoplayTimeout();
    };
  }, [content.autoplay, goToNextItem]);

  const handleMouseEnter = () => {
    isHovered.current = true;
    clearAutoplayTimeout();
  };

  const handleMouseLeave = () => {
    isHovered.current = false;
    if (content.autoplay) {
      clearAutoplayTimeout();
      autoplayTimeoutRef.current = setTimeout(() => {
        goToNextItem();
      }, RESUME_DELAY);
    }
  };

  const onItemMouseEnter = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className={slots.base({ className })}>
      <div
        className={
          content.type === 'image' ? slots.rowImage() : slots.rowText()
        }
      >
        {content.type === 'image' && (
          <div className={slots.visual()}>
            {content.items.map((item, index) => (
              <div
                key={index}
                className={slots.visualWrap({
                  activeImage: activeIndex === index
                })}
              >
                {item.image && (
                  <Image
                    className={slots.image()}
                    alt=""
                    width="480"
                    height="480"
                    src={item.image}
                  />
                )}

                {item.showLinkButton && (
                  <Link
                    href={item.link}
                    className={slots.visualLink()}
                    target={item.isBlank ? '_blank' : undefined}
                    rel={utilGetLinkRel(item.link, item.isBlank)}
                  >
                    <span
                      style={{
                        color: item.linkColor
                      }}
                    >
                      {item.linkText}
                    </span>

                    <span
                      className={slots.visualLinkIcon()}
                      style={{
                        background: item.iconColor
                      }}
                    >
                      <ArrowIcon />
                    </span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
        {content.type === 'text' && (
          <div className={slots.main()}>
            {content.title && <p className={slots.title()}>{content.title}</p>}
            {content.subtitle && (
              <p className={slots.subtitle()}>{content.subtitle}</p>
            )}

            {content.text && (
              <p
                className={slots.text()}
                dangerouslySetInnerHTML={{ __html: content.text }}
              ></p>
            )}
            {content.linkUrl && content.linkText && (
              <a
                href={content.linkUrl}
                className={slots.link()}
              >
                {content.linkText}
                <ArrowIcon />
              </a>
            )}
          </div>
        )}

        <div className={slots.items()}>
          {content.itemsTitle && (
            <h3 className={slots.itemsTitle()}>{content.itemsTitle}</h3>
          )}
          <div
            className={slots.itemsList()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {content.items.map((item, index) => (
              <Link
                key={item.id}
                href={item.link}
                className={clsx(
                  slots.item({ activeItem: activeIndex === index }),
                  itemClass
                )}
                onMouseEnter={() => onItemMouseEnter(index)}
                onClick={() => {
                  if (!item.gtmEvent) return;

                  sendGTMEvent({
                    event: item.gtmEvent,
                    eventCategory: item.gtmEventCategory,
                    eventAction: item.gtmEventAction,
                    eventLabel: item.gtmEventLabel
                  } as Parameters<typeof sendGTMEvent>[0]);
                }}
                target={item.isBlank ? '_blank' : '_self'}
                rel={utilGetLinkRel(item.link, item.isBlank)}
              >
                <Image
                  className={slots.itemImage()}
                  alt={item.text}
                  width="200"
                  height="200"
                  src={item.image}
                />
                <div className={slots.info()}>
                  {item.brand === 'bioderma' && (
                    <BiodermaIcon className={slots.iconBioderma()} />
                  )}
                  {item.brand === 'esthederm' && (
                    <EsthedermIcon className={slots.iconEsthederm()} />
                  )}
                  {item.brand === 'etatpur' && (
                    <EtatPurIcon className={slots.iconEtatpur()} />
                  )}

                  {item.title && (
                    <p
                      style={{ color: item.colorTitle }}
                      className={slots.infoTitle()}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    ></p>
                  )}

                  <p className={slots.infoText()}>{item.text}</p>
                  <ChevronIcon className={slots.arrow()} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
